import { Injectable } from '@angular/core';

import { of } from 'rxjs';
import { map, mergeMap, catchError, concatMap, withLatestFrom } from 'rxjs/operators';

import { Store, select } from '@ngrx/store';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { RouterReducerState } from '@ngrx/router-store';

import { RouterStateUrl } from '@sd-portal/shared/util';

import { TrainingRequirementAuditService } from '../../training-requirement-audit.service';
import { State } from '..';
import * as actions from './training-requirement.actions';

@Injectable()
export class TrainingRequirementEffects {
  constructor(
    private readonly trainingRequirementAuditService: TrainingRequirementAuditService,
    private readonly actions$: Actions,
    private readonly store: Store<{
      router: RouterReducerState<RouterStateUrl>;
      sharedAudit: State;
    }>
  ) {}

  // Skeleton
  loadTrainingRequirementSkeleton$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadTrainingRequirementSkeletons),
      mergeMap(() =>
        this.trainingRequirementAuditService.getTrainingRequirementSkeletons().pipe(
          map(payload => actions.TrainingRequirementSkeletonsLoaded({ payload })),
          catchError(err => of(actions.TrainingRequirementSkeletonsLoadError(err)))
        )
      )
    )
  );

  loadTrainingGroupSkeleton$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadTrainingGroupSkeletons),
      mergeMap(() =>
        this.trainingRequirementAuditService.getTrainingGroupSkeletons().pipe(
          map(payload => actions.TrainingGroupSkeletonsLoaded({ payload })),
          catchError(err => of(actions.TrainingGroupSkeletonsLoadError(err)))
        )
      )
    )
  );

  loadBusinessPersonWithTrainingSkeletons$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadBusinessPersonWithTrainingSkeletons),
      mergeMap(() =>
        this.trainingRequirementAuditService.getBusinessPersonTrainingRequirementSkeletons().pipe(
          map(payload => actions.BusinessPersonWithTrainingSkeletonsLoaded({ payload })),
          catchError(err => of(actions.BusinessPersonWithTrainingSkeletonsLoadError(err)))
        )
      )
    )
  );

  // TrainingRequirement
  loadTrainingRequirementAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadTrainingRequirementAudit),
      mergeMap(action =>
        this.trainingRequirementAuditService
          .getTrainingRequirementAudits(action.trainingRequirementId)
          .pipe(
            map(audits =>
              actions.TrainingRequirementAuditLoaded({
                trainingRequirementId: action.trainingRequirementId,
                audits
              })
            ),
            catchError(err => of(actions.TrainingRequirementAuditLoadError(err)))
          )
      )
    )
  );

  loadNextPageTrainingRequirementAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageTrainingRequirementAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId =
            state.sharedAudit.trainingRequirement.trainingRequirementAudit.lastAuditLogId;
        }
        return this.trainingRequirementAuditService
          .getTrainingRequirementAudits(
            action.trainingRequirementId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.TrainingRequirementAuditLoaded({
                trainingRequirementId: action.trainingRequirementId,
                audits
              })
            ),
            catchError(err => of(actions.TrainingRequirementAuditLoadError(err)))
          );
      })
    )
  );

  // TrainingGroup
  loadTrainingGroupAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadTrainingGroupAudit),
      mergeMap(action =>
        this.trainingRequirementAuditService.getTrainingGroupAudits(action.trainingGroupId).pipe(
          map(audits =>
            actions.TrainingGroupAuditLoaded({
              trainingGroupId: action.trainingGroupId,
              audits
            })
          ),
          catchError(err => of(actions.TrainingGroupAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageTrainingGroupAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageTrainingGroupAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.trainingRequirement.trainingGroupAudit.lastAuditLogId;
        }
        return this.trainingRequirementAuditService
          .getTrainingGroupAudits(action.trainingGroupId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.TrainingGroupAuditLoaded({
                trainingGroupId: action.trainingGroupId,
                audits
              })
            ),
            catchError(err => of(actions.TrainingGroupAuditLoadError(err)))
          );
      })
    )
  );

  // TrainingGroupTrainingRequirements
  loadTrainingGroupTrainingRequirementAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadTrainingGroupTrainingRequirementAudit),
      mergeMap(action =>
        this.trainingRequirementAuditService
          .getTrainingGroupTrainingRequirementAudits(action.trainingGroupTrainingRequirementId)
          .pipe(
            map(audits =>
              actions.TrainingGroupTrainingRequirementAuditLoaded({
                trainingGroupTrainingRequirementId: action.trainingGroupTrainingRequirementId,
                audits
              })
            ),
            catchError(err => of(actions.TrainingGroupTrainingRequirementAuditLoadError(err)))
          )
      )
    )
  );

  loadNextPageTrainingGroupTrainingRequirementAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageTrainingGroupTrainingRequirementAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId =
            state.sharedAudit.trainingRequirement.trainingGroupTrainingRequirementAudit
              .lastAuditLogId;
        }
        return this.trainingRequirementAuditService
          .getTrainingGroupTrainingRequirementAudits(
            action.trainingGroupTrainingRequirementId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.TrainingGroupTrainingRequirementAuditLoaded({
                trainingGroupTrainingRequirementId: action.trainingGroupTrainingRequirementId,
                audits
              })
            ),
            catchError(err => of(actions.TrainingGroupTrainingRequirementAuditLoadError(err)))
          );
      })
    )
  );

  // BusinessPersonTrainingRequirement
  loadBusinessPersonTrainingRequirementAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadBusinessPersonTrainingRequirementAudit),
      mergeMap(action =>
        this.trainingRequirementAuditService
          .getBusinessPersonTrainingRequirementAudits(action.businessPersonTrainingRequirementId)
          .pipe(
            map(audits =>
              actions.BusinessPersonTrainingRequirementAuditLoaded({
                businessPersonTrainingRequirementId: action.businessPersonTrainingRequirementId,
                audits
              })
            ),
            catchError(err => of(actions.TrainingRequirementAuditLoadError(err)))
          )
      )
    )
  );

  loadNextPageBusinessPersonTrainingRequirementAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageBusinessPersonTrainingRequirementAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId =
            state.sharedAudit.trainingRequirement.businessPersonTrainingRequirementAudit
              .lastAuditLogId;
        }
        return this.trainingRequirementAuditService
          .getBusinessPersonTrainingRequirementAudits(
            action.businessPersonTrainingRequirementId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.BusinessPersonTrainingRequirementAuditLoaded({
                businessPersonTrainingRequirementId: action.businessPersonTrainingRequirementId,
                audits
              })
            ),
            catchError(err => of(actions.BusinessPersonTrainingRequirementAuditLoadError(err)))
          );
      })
    )
  );

  // Warning Rule
  loadWarningRuleAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadWarningRuleAudit),
      mergeMap(action =>
        this.trainingRequirementAuditService.getWarningRuleAudits(action.warningRuleId).pipe(
          map(audits =>
            actions.WarningRuleAuditLoaded({ warningRuleId: action.warningRuleId, audits })
          ),
          catchError(err => of(actions.WarningRuleAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageWarningRuleAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageWarningRuleAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.trainingRequirement.warningRuleAudit.lastAuditLogId;
        }
        return this.trainingRequirementAuditService
          .getWarningRuleAudits(action.warningRuleId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.WarningRuleAuditLoaded({ warningRuleId: action.warningRuleId, audits })
            ),
            catchError(err => of(actions.WarningRuleAuditLoadError(err)))
          );
      })
    )
  );
}
